import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, HandCoins, MessageCircle, TrendingUp, TrendingDown, Percent, CalendarClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { openWhatsApp } from "../../lib/whatsapp";
import { inputCls } from "./shared";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const todayStr = () => new Date().toLocaleDateString("en-CA");
const SOURCE_ACCOUNTS = ["Salon Cash Drawer (Counter)", "Salon Business Bank Account", "Papa Ji's Account / UPI", "Mummy Ji's Account / UPI", "Personal Self Account / UPI"];
const emptyLoan = (type) => ({ type, person_name: "", phone: "", principal: "", account: SOURCE_ACCOUNTS[0], interest_enabled: false, interest_rate: "", interest_cycle: "month", start_date: todayStr(), due_date: "", notes: "" });

export const FinanceUdhaar = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLoan("given"));
  const [paying, setPaying] = useState(null);
  const [payForm, setPayForm] = useState({ principal: "", interest: "", note: "" });

  const load = useCallback(() => api.get("/loans").then(({ data }) => setData(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

  const openNew = (type) => { setForm(emptyLoan(type)); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.person_name || !Number(form.principal)) { toast.error("Person name and principal amount required"); return; }
    try {
      await api.post("/loans", { ...form, principal: Number(form.principal), interest_rate: Number(form.interest_rate) || 0 });
      toast.success(form.type === "given" ? "Udhaar Diya recorded" : "Udhaar Liya recorded");
      setOpen(false); load();
    } catch (e2) { err(e2); }
  };

  const savePayment = async (e) => {
    e.preventDefault();
    const principal = Number(payForm.principal) || 0;
    const interest = Number(payForm.interest) || 0;
    if (principal <= 0 && interest <= 0) { toast.error("Enter principal or interest amount"); return; }
    try {
      const { data: updated } = await api.post(`/loans/${paying.id}/payment`, { principal, interest, note: payForm.note });
      toast.success(updated.status === "closed" ? "Loan fully settled 🎉" : "Payment recorded");
      setPaying(null); setPayForm({ principal: "", interest: "", note: "" }); load();
    } catch (e2) { err(e2); }
  };

  const remove = async (id) => {
    try { await api.delete(`/loans/${id}`); toast.success("Loan entry deleted"); load(); } catch (e2) { err(e2); }
  };

  const remind = (l) => {
    const who = l.type === "given" ? "aapke" : "humare";
    const msg = `Namaste ${l.person_name} ji 🙏\nGREEN Professionals Salon ki taraf se yaad dilana chahte hain — ${who} udhaar ke ${fmt(l.total_balance)} (principal ${fmt(l.outstanding)}${l.interest_due > 0 ? ` + interest ${fmt(l.interest_due)}` : ""}) ${l.due_date ? `ki repayment date ${l.due_date} hai` : "pending hain"}. Dhanyavaad!`;
    openWhatsApp(l.phone, msg);
  };

  if (!data) return <p className="text-sm text-[#5C6A63]" data-testid="udhaar-loading">Loading…</p>;
  const s = data.summary;

  const cards = [
    { label: "Udhaar Diya (Receivables)", value: s.given_outstanding + s.given_interest, sub: `principal ${fmt(s.given_outstanding)} + interest ${fmt(s.given_interest)}`, icon: TrendingUp, color: "text-emerald-700" },
    { label: "Udhaar Liya (Payables)", value: s.taken_outstanding + s.taken_interest, sub: `principal ${fmt(s.taken_outstanding)} + interest ${fmt(s.taken_interest)}`, icon: TrendingDown, color: "text-red-600" },
    { label: "Accrued Interest till Date", value: s.accrued_total, sub: "auto-calculated on active loans", icon: Percent, color: "text-[#8a6d33]" },
    { label: "Upcoming Dues (7 days)", value: s.upcoming_count, sub: "repayment dates approaching", icon: CalendarClock, color: "text-blue-700", plain: true },
  ];

  const rowTint = (l) => {
    if (l.outstanding <= 0) return "";
    if (l.overdue) return "bg-red-50";
    if (l.days_left !== null && l.days_left <= 3) return "bg-[#C5A059]/10";
    return "";
  };

  return (
    <div className="space-y-5" data-testid="udhaar-tab">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-[#E5E7E5] rounded-md p-5 shadow-sm" data-testid={`udhaar-card-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs uppercase tracking-wider text-[#5C6A63] font-semibold">{c.label}</p>
              <c.icon className="w-4 h-4 text-[#C5A059]" />
            </div>
            <p className={`text-2xl font-bold ${c.color}`}>{c.plain ? c.value : fmt(c.value)}</p>
            <p className="text-[11px] text-[#5C6A63] mt-1">{c.sub}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => openNew("given")} data-testid="add-loan-given-btn"
          className="bg-emerald-600 text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-emerald-700">
          <Plus className="w-4 h-4" /> Record Udhaar Diya (Loan Given)
        </button>
        <button onClick={() => openNew("taken")} data-testid="add-loan-taken-btn"
          className="bg-red-600 text-white text-sm font-semibold px-4 py-2.5 rounded-full flex items-center gap-2 hover:bg-red-700">
          <Plus className="w-4 h-4" /> Record Udhaar Liya (Loan Taken)
        </button>
      </div>

      <div className="bg-white border border-[#E5E7E5] rounded-md shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E7E5] text-left text-xs uppercase tracking-wider text-[#5C6A63]">
              <th className="px-4 py-3 font-semibold">Person</th>
              <th className="px-4 py-3 font-semibold">Type</th>
              <th className="px-4 py-3 font-semibold">Principal</th>
              <th className="px-4 py-3 font-semibold">Interest</th>
              <th className="px-4 py-3 font-semibold">Accrued</th>
              <th className="px-4 py-3 font-semibold">Total Balance</th>
              <th className="px-4 py-3 font-semibold">Due Date</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="loans-table-body">
            {data.rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[#5C6A63]" data-testid="no-loans">No udhaar entries yet.</td></tr>}
            {data.rows.map((l) => (
              <tr key={l.id} className={`border-b border-[#E5E7E5] last:border-0 ${rowTint(l)}`} data-testid={`loan-row-${l.id}`}>
                <td className="px-4 py-3">
                  <p className="font-semibold text-[#1C1C1C]">{l.person_name}</p>
                  <p className="text-xs text-[#5C6A63]">{l.phone || "—"}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${l.type === "given" ? "bg-emerald-600/10 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                    {l.type === "given" ? "Given" : "Taken"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <p className="font-semibold">{fmt(l.outstanding)}</p>
                  {l.principal_repaid > 0 && <p className="text-[10px] text-[#5C6A63]">of {fmt(l.principal)} · repaid {fmt(l.principal_repaid)}</p>}
                </td>
                <td className="px-4 py-3 text-xs text-[#5C6A63]">{l.interest_enabled ? `${l.interest_rate}% / ${l.interest_cycle === "month" ? "mo" : "yr"}` : "No interest"}</td>
                <td className="px-4 py-3 font-semibold text-[#8a6d33]">{l.interest_enabled ? fmt(l.accrued_interest) : "—"}</td>
                <td className="px-4 py-3 font-bold text-[#0A3F2F]" data-testid={`loan-balance-${l.id}`}>{fmt(l.total_balance)}</td>
                <td className="px-4 py-3 text-xs">
                  {l.due_date || "—"}
                  {l.outstanding > 0 && l.overdue && <p className="text-[10px] font-bold text-red-600">OVERDUE</p>}
                  {l.outstanding > 0 && !l.overdue && l.days_left !== null && l.days_left <= 3 && <p className="text-[10px] font-bold text-[#8a6d33]">in {l.days_left} day{l.days_left === 1 ? "" : "s"}</p>}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full ${l.status === "closed" ? "bg-gray-100 text-gray-500" : "bg-[#0A3F2F]/10 text-[#0A3F2F]"}`}>
                    {l.status === "closed" ? "Settled" : "Active"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-3">
                    {l.status !== "closed" && (
                      <button onClick={() => { setPaying(l); setPayForm({ principal: String(l.outstanding), interest: l.interest_due > 0 ? String(l.interest_due) : "", note: "" }); }}
                        data-testid={`loan-pay-${l.id}`} title="Record payment" className="text-[#C5A059] hover:text-[#0A3F2F]"><HandCoins className="w-4 h-4" /></button>
                    )}
                    {l.phone && l.status !== "closed" && (
                      <button onClick={() => remind(l)} data-testid={`loan-remind-${l.id}`} title="Send WhatsApp reminder"
                        className="text-[#3D4A44] hover:text-emerald-600"><MessageCircle className="w-4 h-4" /></button>
                    )}
                    <button onClick={() => remove(l.id)} data-testid={`loan-delete-${l.id}`} title="Delete"
                      className="text-[#3D4A44] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#5C6A63]">Loan principal is never counted as salon income or expense — only interest received/paid affects Net Profit.</p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{form.type === "given" ? "Record Udhaar Diya (Loan Given)" : "Record Udhaar Liya (Loan Taken)"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3" data-testid="loan-form">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="loan-name-input" className={inputCls} placeholder="Person name *" value={form.person_name} onChange={(e) => setForm({ ...form, person_name: e.target.value })} />
              <input data-testid="loan-phone-input" className={inputCls} placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <input data-testid="loan-principal-input" type="number" min="1" step="any" className={inputCls} placeholder="Principal amount ₹ *" value={form.principal} onChange={(e) => setForm({ ...form, principal: e.target.value })} />
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">{form.type === "given" ? "Paid from" : "Received in"}</label>
              <select data-testid="loan-account-select" className={inputCls} value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })}>
                {SOURCE_ACCOUNTS.map((a) => <option key={a}>{a}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" data-testid="loan-interest-toggle" checked={form.interest_enabled} onChange={(e) => setForm({ ...form, interest_enabled: e.target.checked })} />
              {form.type === "given" ? "Charge interest?" : "Pay interest?"}
            </label>
            {form.interest_enabled && (
              <div className="grid grid-cols-2 gap-3 bg-[#C5A059]/10 border border-[#C5A059]/30 rounded-md p-3">
                <input data-testid="loan-rate-input" type="number" min="0" step="any" className={inputCls} placeholder="Interest rate %" value={form.interest_rate} onChange={(e) => setForm({ ...form, interest_rate: e.target.value })} />
                <select data-testid="loan-cycle-select" className={inputCls} value={form.interest_cycle} onChange={(e) => setForm({ ...form, interest_cycle: e.target.value })}>
                  <option value="month">Per Month</option>
                  <option value="year">Per Year</option>
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Start date</label>
                <input data-testid="loan-start-input" type="date" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Repayment / reminder date</label>
                <input data-testid="loan-due-input" type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
            </div>
            <input data-testid="loan-notes-input" className={inputCls} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <button type="submit" data-testid="loan-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Save Entry
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!paying} onOpenChange={(v) => !v && setPaying(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Record Payment — {paying?.person_name}</DialogTitle></DialogHeader>
          <form onSubmit={savePayment} className="space-y-3" data-testid="loan-payment-form">
            <div className="bg-[#FAFAF8] rounded-md p-3 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6A63]">Outstanding principal</span><span className="font-semibold">{fmt(paying?.outstanding)}</span></div>
              <div className="flex justify-between"><span className="text-[#5C6A63]">Interest due</span><span className="font-semibold text-[#8a6d33]">{fmt(Math.max(paying?.interest_due || 0, 0))}</span></div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Principal {paying?.type === "given" ? "received" : "repaid"} ₹</label>
              <input data-testid="loan-pay-principal-input" type="number" min="0" step="any" className={inputCls} value={payForm.principal} onChange={(e) => setPayForm({ ...payForm, principal: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5C6A63] font-semibold">Interest {paying?.type === "given" ? "received (income)" : "paid (expense)"} ₹</label>
              <input data-testid="loan-pay-interest-input" type="number" min="0" step="any" className={inputCls} value={payForm.interest} onChange={(e) => setPayForm({ ...payForm, interest: e.target.value })} />
            </div>
            <input data-testid="loan-pay-note-input" className={inputCls} placeholder="Note (optional)" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} />
            <button type="submit" data-testid="loan-pay-submit-btn" className="w-full bg-[#0A3F2F] text-white font-semibold py-2.5 rounded-full text-sm hover:bg-[#0d5540]">
              Record Payment
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
